import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ROUTE_REGION_AND_BUILDING_DATA,
    ROUTE_EDIT_BUILDING,
    ROUTE_ADD_BUILDING
} from '../Routes/Routes';
import usePageTitle from '../../hooks/usePageTitle';
import {
    Box,
} from '@mui/material';
import GetFetch from '../../hooks/GetFetch';
import CustomStore from 'devextreme/data/custom_store';
import DataGrid, {
    KeyboardNavigation,
    Pager,
    Paging,
    SearchPanel,
    Selection,
    Column,
    Export
} from 'devextreme-react/data-grid';
import CustomPreparedHeaderCell from '../../components/CustomPreparedHeaderCell';
import { logExport, useAuth } from '../../hooks/useAuth';
import ExportGrid from '../../components/ExportGrid';
import { allowedPageSizes, pageFormats } from "../../components/GlobalDataGridConfigurations";
import ClosingAlert from '../../components/ClosingAlert';
import { BaseButton } from '../../components/BaseButton/BaseButton';
import { Content } from '../../Enums/contents';
import useAriaLiveMatchCount from '../../components/UseAriaLiveMatchCount';

const {
    Buttons: {
        newBuildingButtonLabel,
        MUI: {
            Variant: {
                outlined
            }
        }
    }
} = Content;

const BuildingsTable = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const { title } = ROUTE_REGION_AND_BUILDING_DATA;
    const { updateMatchCount, LiveRegion } = useAriaLiveMatchCount();
    const gridRef = useRef(null);

    usePageTitle(title);

    const fetchBuildings = () => {
        return GetFetch('/v1/Building')
    };

    const dataSource = new CustomStore({
        key: 'BuildingNumber',
        load: async () => {
            const { Message } = await fetchBuildings();
            let buildingsData = [];
            
            if (Message) {
                buildingsData = Message.map(
                    (
                        {
                            SequenceNumber,
                            BuildingId,
                            Name,
                            Facility,
                            Status,
                            RegionNumber,
                            Location: {
                                Id,
                                Address1,
                                Address2,
                                CityName,
                                State,
                                ZipCode
                            },
                            RentUsageFactor,
                            AdjustedRentSqFt,
                            AdjustedUsageSqFt,
                            AnsiRentSqFt,
                            AnsiUsableSqFt,
                            JointUseSqFt,
                            CommunityJointUseSqFt,
                            LeaseJointUseSqFt,
                            CongressionalRep,
                            Version
                        }
                    ) => {
                        return {
                            BuildingNumber: SequenceNumber,
                            BuildingId: BuildingId,
                            name: Name,
                            status: Status,
                            facility: Facility,
                            region: RegionNumber,
                            locationId: Id,
                            address: `${Address1},${Address2 !== '' ? ' ' + Address2 + ',' : ''} ${CityName}, ${State} ${ZipCode}`,
                            rentUsageFactor: RentUsageFactor,
                            adjustedRent: AdjustedRentSqFt,
                            adjustedUsage: AdjustedUsageSqFt,
                            ansiRent: AnsiRentSqFt,
                            ansiUsable: AnsiUsableSqFt,
                            jointUse: JointUseSqFt,
                            communityJointUse: CommunityJointUseSqFt,
                            leaseJointUse: LeaseJointUseSqFt,
                            congressionalRep: CongressionalRep,
                            version: Version
                        };
                    }
                );
            }

            // THIS LINE IS BREAKING THE CODE. UPDATE MATCHCOUNT
            // updateMatchCount(buildingsData.length);
            return buildingsData;
        }
    });

    const routeToEditBuildingModule = ({ data }) => {
        const selectedRowData = data;
        navigate(ROUTE_EDIT_BUILDING.withSlash, {
            state: { selectedRowData }
        });
    };

    const renderCellEditLabel = (e) => {
        const buildingNumber = e.data.BuildingNumber;
        return (
            <a
                onClick={() => {
                    routeToEditBuildingModule(e)
                }}
            >
                {
                    'Edit: ' + buildingNumber
                }
            </a>

        );
    };

    const handleRouteToAddBuildingModule = () => navigate(ROUTE_ADD_BUILDING.withSlash)

    const pdfFont = () => { return 6; }; // accept current change
    const wrapPdfCell = () => {
        return true;
    };

    const handleExport = (e) => {
        logExport(user.email);
        ExportGrid(e, "Buildings_Data", wrapPdfCell(), pdfFont())
    };

    return (
        <div>
            <ClosingAlert
                severity="error"
                message={errorMessage}
                visible={error}
                hideAlert={() => { setError(false) }}
            />
            <div>
                <Box sx={{ height: 45, width: '97%', margin: 'auto auto 0 auto' }}>
                    <BaseButton
                        id="new-building-button"
                        label={newBuildingButtonLabel}
                        onClick={handleRouteToAddBuildingModule}  // accept current change
                        variant={outlined}
                    />
                </Box>
                <Box sx={{ height: 700, width: '97%', margin: 'auto' }}>
                    <span
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        <LiveRegion />
                        <DataGrid
                            ref={gridRef}
                            columnAutoWidth={true}
                            dataSource={dataSource}
                            focusedRowEnabled={true}
                            keyboardNavigation={{
                                enabled: true,
                                enterKeyAction: 'none',
                                enterKeyDirection: 'none'
                            }}
                            onContentReady={(e) => {
                                const visibleRows = e.component.getVisibleRows();
                                updateMatchCount(visibleRows.length);
                            }}
                            onOptionChanged={(e) => {
                                if (e.fullName === 'searchPanel.text' && gridRef.current) {
                                    const visibleRows = gridRef.current.instance.getVisibleRows();
                                    updateMatchCount(visibleRows.length);
                                }
                            }}
                            onCellPrepared={CustomPreparedHeaderCell}
                            onExporting={e => handleExport(e)}
                            rowAlternationEnabled={true}
                            showBorders={true}
                            showColumnLines={true}
                            showRowLines={true}
                        >
                            <KeyboardNavigation enabled={true} />
                            <SearchPanel visible={true} highlightCaseSensitive={true} />
                            <Selection mode="single" />
                            <Paging defaultPageSize={10} />
                            <Pager
                                showPageSizeSelector={true}
                                allowedPageSizes={allowedPageSizes}
                            />
                            <Column
                                dataField="Number"
                                captian=""
                                name="Edit"
                                cellRender={(e) => renderCellEditLabel(e)}
                            />
                            <Column dataField="BuildingId" visible={false} />
                            <Column dataField="locationId" visible={false} />
                            <Column dataField="version" visible={false} />
                            <Column dataField="region" caption="Region" />
                            <Column dataField="BuildingNumber" caption="Building Number" />
                            <Column dataField="status" caption="Status" />
                            <Column dataField="name" caption="Name" />
                            <Column dataField="facility" caption="Facility" />
                            <Column dataField="address" caption="Address" />
                            <Column dataField="rentUsageFactor" caption="Rent Usage Factor" />
                            <Column dataField="adjustedRent" caption="Adjusted Rent" />
                            <Column dataField="adjustedUsage" caption="Adjusted Usage" />
                            <Column dataField="ansiRent" caption="ANSI Rent" format="fixedPoint" />
                            <Column dataField="ansiUsable" caption="ANSI Usable" />
                            <Column dataField="jointUse" caption="Joint Use" />
                            <Column dataField="communityJointUse" caption="Community Joint Use" />
                            <Column dataField="leaseJointUse" caption="Lease Joint Use" />
                            <Column dataField="congressionalRep" caption="Congressional Rep" />
                            <Export
                                enabled={true}
                                formats={pageFormats}
                            />
                        </DataGrid>

                    </span>
                </Box>
            </div>
        </div>
    );
};
export default BuildingsTable;